import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories, heroSlides } from '@/mocks/movies';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const hotMovies = movies.filter(m => m.isHot).slice(0, 8);
  const newMovies = movies.filter(m => m.isNew).slice(0, 8);
  const mainCategories = categories.filter(c => c.slug !== 'all').slice(0, 8);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
          {heroSlides.map((slide, index) => {
            const movie = movies.find(m => m.id === slide.movieId);
            return (
              <Link
                key={slide.id}
                to={movie ? `/movie/${movie.slug}` : '/'}
                className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                <img
                  src={slide.coverUrl}
                  alt={slide.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
                <div className="absolute inset-0 flex items-center">
                  <div className="max-w-[1400px] mx-auto w-full px-4 md:px-6">
                    <div className="max-w-xl">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full mb-4 whitespace-nowrap">
                        正在热播
                      </span>
                      <h2 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-3">
                        {slide.title}
                      </h2>
                      <p className="text-sm md:text-base text-background-200 mb-6 leading-relaxed">
                        {slide.subtitle}
                      </p>
                      <span className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer">
                        <i className="ri-play-fill text-lg"></i>
                        立即观看
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}

          <button
            onClick={prevSlide}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 backdrop-blur-sm transition-all cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/40 text-background-50 backdrop-blur-sm transition-all cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${index === currentSlide ? 'bg-primary-500 w-6' : 'bg-background-50/50'}`}
                aria-label={`第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Entry */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="分类浏览">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">分类浏览</h2>
                <p className="text-sm text-foreground-500 mt-1">按类型找到你喜欢的电影</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line ml-1 text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
              {mainCategories.map(cat => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 md:p-5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer group"
                >
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                    <i className={`${cat.icon} text-lg md:text-xl`}></i>
                  </span>
                  <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-primary-600 whitespace-nowrap transition-colors">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Recommend */}
        <section className="py-10 md:py-14 bg-background-100" aria-label="热门推荐">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">热门推荐</h2>
                <p className="text-sm text-foreground-500 mt-1">千万用户正在观看的热门大片</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {hotMovies.map(movie => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  slug={movie.slug}
                  title={movie.title}
                  year={movie.year}
                  category={movie.category}
                  rating={movie.rating}
                  posterUrl={movie.posterUrl}
                  isVip={movie.isVip}
                  isNew={movie.isNew}
                />
              ))}
            </div>
          </div>
        </section>

        {/* New Arrivals */}
        <section className="py-10 md:py-14 bg-background-50" aria-label="最新上架">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">最新上架</h2>
                <p className="text-sm text-foreground-500 mt-1">第一时间更新，不错过任何精彩</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {newMovies.map(movie => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  slug={movie.slug}
                  title={movie.title}
                  year={movie.year}
                  category={movie.category}
                  rating={movie.rating}
                  posterUrl={movie.posterUrl}
                  isVip={movie.isVip}
                  isNew={movie.isNew}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-100" aria-label="为什么选择屁屁影院">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">为什么选择屁屁影院？</h2>
            <p className="text-sm text-foreground-500 mb-10">我们致力于为用户提供极致的免费观影体验</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: "ri-database-2-line", title: "海量片源", desc: "50000+部电影资源，涵盖动作、喜剧、科幻、爱情等各类题材，满足不同观影需求" },
                { icon: "ri-4k-line", title: "高清画质", desc: "1080P超清画质，智能码率调节技术，在保证画质的同时实现流畅播放" },
                { icon: "ri-flashlight-line", title: "秒速加载", desc: "全球CDN加速节点覆盖，智能线路优化，确保即点即播，告别漫长等待" },
                { icon: "ri-vip-crown-line", title: "完全免费", desc: "无需注册、无需付费，所有电影资源免费开放，轻松享受观影乐趣" }
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-left">
                  <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-500 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "屁屁影院",
            "url": "https://www.pipicinema.com",
            "description": "屁屁影院作为知名免费影视站，汇聚电影大全，高清免费在线观看不缓冲，最新大片即点即播，热门经典应有尽有，秒速播放，极速加载，追剧观影更畅快。",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.pipicinema.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })
        }}
      />
    </>
  );
}